/**
 * Which category each authored map belongs to. Pure metadata — `index.ts`
 * still statically imports `home_settlement` directly (see
 * `production/session-state/active.md`'s "raid maps are not reachable in
 * play" known issue), so this registry does not yet drive map selection.
 * It exists so a future map-selection seam (and the two raid maps, once
 * routed into play) has one place that says what a map IS, instead of
 * inferring it from a filename.
 *
 * `raid` has six now, one per world-map region — see `MapRegistryEntry.region`.
 * Four of them are baked by `scripts/author_region_raid_maps.ts`; none of the
 * six is reachable in play yet, for the reason below.
 *
 * `battleground` has two now: `arena_pit` ("The Pit") and `arena_scrapyard`
 * ("The Scrapyard"), the maps `ArenaService.ARENA_MAP_KEYS` picks one of per
 * lobby. Neither is actually rendered by a live duel yet — `base-view/
 * index.ts`'s own doc comment on `arena:loaded` says the client streams
 * moves over the socket with no scene load at all, so the "renders it" this
 * comment used to claim (an `arena-view.ts` that does not exist) was
 * aspirational. Both are still real, tested `WorldData` — walkable today
 * through `ultra_map_editor`, ready to render whenever that scene ships.
 */
import type { WorldData } from '../../types';
import { map as arenaPit } from './arena_pit';
import { map as arenaScrapyard } from './arena_scrapyard';
import { map as homeSettlement } from './home_settlement';
import { map as raidCommTowerRidge } from './raid_comm_tower_ridge';
import { map as raidDeepWasteland } from './raid_deep_wasteland';
import { map as raidHighwayJam } from './raid_highway_jam';
import { map as raidIndustrialPark } from './raid_industrial_park';
import { map as raidSuburbanStreets } from './raid_suburban_streets';
import { map as raidSubwayDistrict } from './raid_subway_district';
import { map as testDummies } from './test_dummies';

export type MapCategory = 'settlement' | 'raid' | 'battleground' | 'debug';

export interface MapRegistryEntry {
  /** Matches the data file's basename (no extension) — the guard test checks this against disk. */
  mapKey: string;
  category: MapCategory;
  data: WorldData;
  /**
   * For `category: 'raid'` — the `WorldMapDef.tiles` key this map is the
   * ground for, i.e. the `districtId` `startRaid(focus, districtId)` is called
   * with. Every region the world map offers has exactly one, and
   * `registry.test.ts` is what makes a seventh region without a map fail here
   * rather than in a player's raid.
   *
   * Which map covers which region is a JUDGEMENT, not a derivation: the two
   * hand-authored maps predate the region split and were written to a theme
   * ("suburban streets", "a highway jam with a bandit camp") rather than to a
   * district. They take the two tier-1 regions because that is what they read
   * as. Reassigning one is an edit to this table and nothing else.
   */
  region?: string;
}

export const MAP_REGISTRY: readonly MapRegistryEntry[] = [
  { mapKey: 'home_settlement', category: 'settlement', data: homeSettlement },
  // Tier 1 — hand-authored (scripts/author_raid_maps.ts, then edited by hand).
  { mapKey: 'raid_suburban_streets', category: 'raid', data: raidSuburbanStreets, region: 'settlement_outskirts' },
  { mapKey: 'raid_highway_jam', category: 'raid', data: raidHighwayJam, region: 'downtown_ruins' },
  // Tiers 2-3 — baked by scripts/author_region_raid_maps.ts. Regenerating one
  // is `yarn vite-node scripts/author_region_raid_maps.ts`; the two above are
  // deliberately NOT in that script, because a re-bake would discard their
  // hand edits.
  { mapKey: 'raid_subway_district', category: 'raid', data: raidSubwayDistrict, region: 'subway_district' },
  { mapKey: 'raid_industrial_park', category: 'raid', data: raidIndustrialPark, region: 'industrial_park' },
  { mapKey: 'raid_deep_wasteland', category: 'raid', data: raidDeepWasteland, region: 'deep_wasteland' },
  { mapKey: 'raid_comm_tower_ridge', category: 'raid', data: raidCommTowerRidge, region: 'comm_tower_ridge' },
  { mapKey: 'arena_pit', category: 'battleground', data: arenaPit },
  { mapKey: 'arena_scrapyard', category: 'battleground', data: arenaScrapyard },
  // `debug` is not a place in the game world — it is an inspection rig, and
  // `index.ts` only serves it in DEV. Kept in the registry anyway so it is
  // covered by the same disk/key guard test as everything else; a debug map
  // that silently stops loading is worse than no debug map.
  { mapKey: 'test_dummies', category: 'debug', data: testDummies },
];

export function mapsInCategory(category: MapCategory): readonly MapRegistryEntry[] {
  return MAP_REGISTRY.filter((entry) => entry.category === category);
}
